import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// KPI definition editor.  locks allowed_sources to ["manual"]
// but exposes the source policy fields so future phases can flip them on.

const UNITS = ["count", "hours", "percent", "currency", "ratio", "score"];
const DIRECTIONS = [
  ["higher_is_better", "Higher is better"],
  ["lower_is_better",  "Lower is better"],
  ["target_band",      "Target band (deviation)"],
];
const SCOPES = ["company", "team", "category", "employee", "location"];

const EMPTY = {
  key: "", name: "", description: "",
  unit: "count", currency_code: "",
  direction: "higher_is_better",
  target_value: null, warning_threshold: null, critical_threshold: null,
  scope: "company", scope_id: "",
  tags: [], is_active: true,
  allowed_sources: ["manual"], preferred_source: "manual",
  // Phase 2Z.s-4 — Performance Focus Lite (additive).
  linked_category_id: "", target_basis: "per_employee",
  target_frequency: "per_week",
  // Mon-Fri default. Weekday indices Monday=0 .. Sunday=6.
  target_days: [0, 1, 2, 3, 4],
  // Phase 2Z.an — applicability indicator. "all_teams" is the
  // default; "selected_teams_only" means the manager wants this
  // activity restricted to its overridden teams. On save we
  // coerce target_value to null in that mode and require at
  // least one team override.
  applies_to: "all_teams",
};

export default function KPIEditorDialog({ open, onOpenChange, editing,
                                            teams, categories, employees, onSaved,
                                            mode = "kpi" }) {
  // Phase 2Z.s-1 — Performance Focus polish. When `mode === "performance"`,
  // the editor hides technical fields (slug, thresholds, scope/tags,
  // preferred source) and renames KPI-flavoured wording to activity-
  // flavoured wording. The underlying API contract is unchanged —
  // hidden fields default silently to safe values.
  const isPerformance = mode === "performance";
  const noun = isPerformance ? "activity" : "KPI";
  const Noun = isPerformance ? "Activity" : "KPI";

  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  // Phase 2Z.am — Performance Focus team overrides. Loaded only
  // when editing an existing Performance Focus activity. Listed
  // below the base target so managers can give specific teams a
  // different number. Strictly additive — legacy KPI flow is
  // unaffected.
  const [overrides, setOverrides] = useState([]);
  const [overridesBusy, setOverridesBusy] = useState(false);
  const [addingOverride, setAddingOverride] = useState(false);
  const [newOverrideTeam, setNewOverrideTeam] = useState("");
  const [newOverrideValue, setNewOverrideValue] = useState("");

  // Auto-generate the technical slug from the activity name when
  // creating a new Performance Focus activity. Managers never have
  // to type a code.
  const _slugify = (s) => (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

  useEffect(() => {
    if (!open) return;
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => setLocations([]));
    if (editing) {
      setForm({
        ...EMPTY, ...editing,
        scope_id: editing.scope_id || "",
        currency_code: editing.currency_code || "",
        tags: editing.tags || [],
        allowed_sources: editing.allowed_sources || ["manual"],
        preferred_source: editing.preferred_source || "manual",
        linked_category_id: editing.linked_category_id || "",
        target_basis: editing.target_basis || "per_employee",
        target_frequency: editing.target_frequency || "per_week",
        target_days: Array.isArray(editing.target_days) && editing.target_days.length > 0
          ? editing.target_days : [0, 1, 2, 3, 4],
        // Phase 2Z.an — derive applicability from the activity.
        // `target_value === null` on a Performance Focus activity
        // signals "Selected teams only" mode. Activities loaded
        // with a numeric target stay in "All teams" mode.
        applies_to: (isPerformance
                     && (editing.target_value === null
                         || editing.target_value === undefined))
                    ? "selected_teams_only"
                    : "all_teams",
      });
    } else {
      // Performance Focus defaults: company-scoped, per-employee
      // basis, count unit (numbers feel more natural than %).
      setForm(isPerformance
        ? { ...EMPTY, scope: "company", target_basis: "per_employee",
            target_frequency: "per_week",
            target_days: [0, 1, 2, 3, 4],
            unit: "count", tags: ["performance_focus"] }
        : EMPTY);
    }
    setTagInput("");
  }, [open, editing, isPerformance]);

  // Phase 2Z.am — load existing team overrides when editing a
  // Performance Focus activity. New activities have no kpi_id yet
  // so the section is hidden until first save.
  const loadOverrides = async () => {
    if (!isPerformance || !editing?.kpi_id) {
      setOverrides([]);
      return;
    }
    try {
      const r = await api.get(`/kpis/${editing.kpi_id}/overrides`);
      setOverrides(Array.isArray(r.data) ? r.data : []);
    } catch {
      setOverrides([]);
    }
  };
  useEffect(() => {
    if (!open) return;
    loadOverrides();
    setAddingOverride(false);
    setNewOverrideTeam("");
    setNewOverrideValue("");
  }, [open, editing?.kpi_id, isPerformance]);

  const overriddenTeamIds = new Set(
    (overrides || [])
      .filter((o) => o?.scope === "team")
      .map((o) => o.scope_id));
  const availableTeamsForOverride = (teams || [])
    .filter((t) => !overriddenTeamIds.has(t.team_id));

  const createOverride = async () => {
    if (!editing?.kpi_id) return;
    if (!newOverrideTeam) return toast.error("Pick a team");
    if (newOverrideValue === "" || newOverrideValue === null
        || Number.isNaN(Number(newOverrideValue))) {
      return toast.error("Enter a target value");
    }
    setOverridesBusy(true);
    try {
      await api.post(`/kpis/${editing.kpi_id}/overrides`, {
        scope: "team",
        scope_id: newOverrideTeam,
        target_value: Number(newOverrideValue),
      });
      toast.success("Team override added");
      setAddingOverride(false);
      setNewOverrideTeam("");
      setNewOverrideValue("");
      await loadOverrides();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setOverridesBusy(false);
    }
  };

  const updateOverrideValue = async (override_id, target_value) => {
    if (!editing?.kpi_id) return;
    if (target_value === "" || Number.isNaN(Number(target_value))) {
      return toast.error("Enter a target value");
    }
    setOverridesBusy(true);
    try {
      await api.patch(
        `/kpis/${editing.kpi_id}/overrides/${override_id}`,
        { target_value: Number(target_value) });
      toast.success("Override updated");
      await loadOverrides();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setOverridesBusy(false);
    }
  };

  const removeOverride = async (override_id) => {
    if (!editing?.kpi_id) return;
    setOverridesBusy(true);
    try {
      await api.delete(`/kpis/${editing.kpi_id}/overrides/${override_id}`);
      toast.success("Override removed");
      await loadOverrides();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setOverridesBusy(false);
    }
  };

  const set = (p) => setForm((prev) => ({ ...prev, ...p }));

  const addTag = () => {
    const v = tagInput.trim();
    if (!v) return;
    if (!(form.tags || []).includes(v)) {
      set({ tags: [...(form.tags || []), v] });
    }
    setTagInput("");
  };
  const removeTag = (t) => set({ tags: (form.tags || []).filter((x) => x !== t) });

  const scopeOptions =
    form.scope === "team" ? teams.map((t) => ({ id: t.team_id, name: t.name }))
    // Phase 2C - scope-by-category restricted to Work categories.
    // Performance KPIs and legacy KPIs both target productive Work
    // activity. Away/Break, Leave and Bank Holiday cannot be a
    // meaningful scope for an "actual vs target" KPI.
    : form.scope === "category" ? categories
        .filter((c) => c.is_active !== false && c.kind === "work")
        .map((c) => ({ id: c.category_id, name: c.name }))
    : form.scope === "employee" ? employees.map((e) => ({ id: e.employee_id, name: e.name }))
    : form.scope === "location" ? locations.map((l) => ({ id: l.location_id, name: l.name }))
    : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(`${Noun} name is required`);
    // Performance Focus auto-generates the slug from the name so
    // managers never see a "code" field. Legacy KPI page still
    // enforces a manually-entered key.
    const effectiveKey = isPerformance
      ? (form.key.trim() || _slugify(form.name) || `act_${Date.now()}`)
      : form.key.trim();
    if (!editing && !effectiveKey) return toast.error("Key is required");
    if (form.unit === "currency" && !form.currency_code) return toast.error("Currency code required when unit=currency");
    if (!isPerformance && form.scope !== "company" && !form.scope_id) {
      return toast.error(`scope_id required for ${form.scope}-scoped KPIs`);
    }
    // Phase 2Z.an — Selected-teams-only mode requires at least one
    // team override. Block save here so the activity is never left
    // in an inconsistent state (target=null + 0 overrides = no
    // employees can record against it).
    if (isPerformance && form.applies_to === "selected_teams_only") {
      if (!editing) {
        return toast.error(
          "Save the activity first, then add team overrides to define which teams it applies to.");
      }
      if (!overrides || overrides.length === 0) {
        return toast.error(
          "Selected teams only requires at least one team override. Add a team below to save.");
      }
    }
    setBusy(true);
    try {
      // Performance Focus tags every activity with "performance_focus"
      // so the page can find them even when linked_category_id /
      // target_value are blank (the manager can fill those in
      // after the fact without losing the activity).
      const baseTags = form.tags || [];
      const effectiveTags = isPerformance
        ? Array.from(new Set([...baseTags, "performance_focus"]))
        : baseTags;
      const body = {
        ...(editing ? {} : { key: effectiveKey }),
        name: form.name.trim(),
        description: form.description || null,
        unit: form.unit,
        currency_code: form.unit === "currency" ? form.currency_code : null,
        direction: form.direction,
        // Phase 2Z.an — coerce target_value to null when the manager
        // selected "Selected teams only" so the snapshot builder
        // (and the dashboard's applicability gate) treat this
        // activity as override-only.
        target_value: (isPerformance
                       && form.applies_to === "selected_teams_only")
                      ? null
                      : (form.target_value ?? null),
        warning_threshold: form.warning_threshold ?? null,
        critical_threshold: form.critical_threshold ?? null,
        // Performance Focus is always company-scoped; the per-employee
        // grid handles individual results via KPIValueIn.scope_id.
        scope: isPerformance ? "company" : form.scope,
        scope_id: isPerformance ? null
                  : (form.scope === "company" ? null : form.scope_id),
        tags: effectiveTags,
        is_active: form.is_active,
        // Performance Focus V1 is manual-entry only. Hide the
        // source policy UI; silently lock to "manual".
        allowed_sources: isPerformance ? ["manual"]
                          : (form.allowed_sources || ["manual"]),
        preferred_source: isPerformance ? "manual"
                          : (form.preferred_source || "manual"),
        // Phase 2Z.s — Performance Focus Lite (additive). Empty
        // strings are normalised to null so the legacy KPI page
        // (which never sets these) stays backward-compatible.
        linked_category_id: form.linked_category_id || null,
        target_basis: form.target_basis || null,
        // Phase 2Z.s-2 — Performance Focus Lite target frequency.
        target_frequency: form.target_frequency || null,
        // Phase 2Z.s-4 — Performance Focus Lite target days. Send
        // the array only when in Performance Focus mode (legacy
        // KPI flow keeps a null here so the schema stays
        // byte-stable for non-performance activities).
        target_days: isPerformance
          ? (Array.isArray(form.target_days) && form.target_days.length > 0
              ? form.target_days : [0, 1, 2, 3, 4])
          : null,
      };
      if (editing) {
        await api.patch(`/kpis/${editing.kpi_id}`, body);
        toast.success(`${Noun} updated`);
      } else {
        await api.post("/kpis", body);
        toast.success(`${Noun} created`);
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" fullscreenOnMobile data-testid="kpi-editor-dialog">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Edit ${noun}` : `New ${noun}`}
          </DialogTitle>
          <DialogDescription>
            {isPerformance
              ? "Track a work activity, set a target, and compare each employee's actual results against the time they were assigned on the rota."
              : "Define what to track and how. Pick a unit, set a target, and decide whether higher is better, lower is better, or you want to stay within a band. Values are entered manually for now."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit}>
          <Tabs defaultValue="basics">
            <TabsList className="bg-[#F6F9FE] border border-slate-200 h-9 p-1 mb-4" data-testid="kpi-tabs">
              <TabsTrigger value="basics" data-testid="kpi-tab-basics"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">
                {isPerformance ? "Activity" : "Basics"}
              </TabsTrigger>
              <TabsTrigger value="targets" data-testid="kpi-tab-targets"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">
                {isPerformance ? "Target" : "Targets & thresholds"}
              </TabsTrigger>
              {/* Scope & tags tab hidden in Performance Focus mode —
                  V1 is always company-scoped and the grid handles
                  per-employee results. */}
              {!isPerformance && (
                <TabsTrigger value="scope" data-testid="kpi-tab-scope"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">Scope &amp; tags</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="basics">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="eyebrow">{isPerformance ? "Activity name" : "Name"}</Label>
                  <Input required value={form.name} data-testid="kpi-name"
                    onChange={(e) => set({ name: e.target.value })}
                    className="h-11 rounded-lg mt-2"
                    placeholder={isPerformance ? "e.g. Incoming calls handled" : "e.g. Average utilisation %"} />
                </div>
                {/* Performance Focus auto-generates the slug from the
                    activity name. Managers never see a "code" field. */}
                {!isPerformance && (
                  <div>
                    <Label className="eyebrow">Key (unique slug)</Label>
                    <Input required value={form.key}
                      disabled={!!editing}
                      data-testid="kpi-key"
                      onChange={(e) => set({ key: e.target.value.toLowerCase() })}
                      className="h-11 rounded-lg mt-2 font-mono text-sm"
                      placeholder="utilisation_pct" />
                  </div>
                )}
                <div>
                  <Label className="eyebrow">Unit of measurement</Label>
                  <Select value={form.unit} onValueChange={(v) => set({ unit: v })}>
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="kpi-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.unit === "currency" && (
                  <div>
                    <Label className="eyebrow">Currency code</Label>
                    <Input value={form.currency_code} data-testid="kpi-currency"
                      onChange={(e) => set({ currency_code: e.target.value.toUpperCase() })}
                      placeholder="GBP" maxLength={6}
                      className="h-11 rounded-lg mt-2" />
                  </div>
                )}
                {/* Performance Focus hides the Status field — V1 only
                    creates active activities. Legacy KPI page keeps it. */}
                {!isPerformance && (
                  <div>
                    <Label className="eyebrow">Status</Label>
                    <Select value={form.is_active ? "active" : "archived"}
                      onValueChange={(v) => set({ is_active: v === "active" })}>
                      <SelectTrigger className="h-11 rounded-lg mt-2"
                        data-testid="kpi-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="eyebrow">Description (optional)</Label>
                  <Input value={form.description} data-testid="kpi-description"
                    onChange={(e) => set({ description: e.target.value })}
                    className="h-11 rounded-lg mt-2" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="targets">
              {/* Phase 2Z.an — "Applies to" picker. Selected-teams-only
                  mode is signalled by `target_value === null` AND at
                  least one team override row. The radio purely
                  drives UI; on save we coerce target_value to null
                  and require at least one override row. */}
              {isPerformance && (
                <div
                  className="mb-3 rounded-lg border border-slate-200 bg-white p-3"
                  data-testid="kpi-applicability-section"
                >
                  <Label className="eyebrow">Applies to</Label>
                  <div className="mt-2 flex flex-col gap-2">
                    <label
                      className="flex items-start gap-2 cursor-pointer"
                      data-testid="kpi-applicability-all"
                    >
                      <input
                        type="radio"
                        name="applies-to"
                        checked={form.applies_to === "all_teams"}
                        onChange={() => set({ applies_to: "all_teams" })}
                        className="mt-1"
                        data-testid="kpi-applicability-all-radio"
                      />
                      <span className="text-sm text-[#0F1B3D]">
                        <span className="font-medium">All teams</span>
                        <span className="block text-[11px] text-[#4B5773]">
                          Every team uses the base target unless you override it below.
                        </span>
                      </span>
                    </label>
                    <label
                      className="flex items-start gap-2 cursor-pointer"
                      data-testid="kpi-applicability-selected"
                    >
                      <input
                        type="radio"
                        name="applies-to"
                        checked={form.applies_to === "selected_teams_only"}
                        onChange={() => {
                          // Phase 2Z.an.1 — switching to selected
                          // mode: force per_employee basis (per_team
                          // and per_company aren't supported in this
                          // mode in V1) AND auto-open the "+ Add
                          // team target" row when there are no
                          // overrides yet, so the manager doesn't
                          // have to take a second click.
                          set({
                            applies_to: "selected_teams_only",
                            target_value: null,
                            target_basis: "per_employee",
                          });
                          if ((overrides || []).length === 0
                              && availableTeamsForOverride.length > 0) {
                            setAddingOverride(true);
                          }
                        }}
                        className="mt-1"
                        data-testid="kpi-applicability-selected-radio"
                      />
                      <span className="text-sm text-[#0F1B3D]">
                        <span className="font-medium">Selected teams only</span>
                        <span className="block text-[11px] text-[#4B5773]">
                          Only the teams you list will be measured on this activity. Other teams will not be judged and cannot have results recorded.
                        </span>
                      </span>
                    </label>
                  </div>
                  {form.applies_to === "selected_teams_only" && (
                    <p
                      className="text-[11px] text-[#4B5773] mt-2"
                      data-testid="kpi-applicability-selected-note"
                    >
                      Add at least one team below. Save will be blocked until you do.
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {/* Goal direction is hidden in Performance Focus —
                    we silently default to "higher is better" which
                    matches the count-based examples we promote. */}
                {!isPerformance && (
                  <div className="col-span-2">
                    <Label className="eyebrow">Goal direction</Label>
                    <Select value={form.direction} onValueChange={(v) => set({ direction: v })}>
                      <SelectTrigger className="h-11 rounded-lg mt-2"
                        data-testid="kpi-direction"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DIRECTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#4B5773] mt-1">
                      Higher is better (e.g. CSAT), lower is better (e.g. absence), or stay within a band.
                    </p>
                  </div>
                )}
                {/* Phase 2Z.an — base target inputs hide when the
                    activity is "Selected teams only" so the manager
                    doesn't see misleading default-target fields. */}
                {(!isPerformance || form.applies_to !== "selected_teams_only") && (
                <div data-testid="kpi-base-target-block">
                  <Label className="eyebrow">Target value</Label>
                  <Input type="number" step="any" data-testid="kpi-target"
                    value={form.target_value ?? ""}
                    onChange={(e) => set({ target_value: e.target.value === "" ? null : Number(e.target.value) })}
                    className="h-11 rounded-lg mt-2"
                    placeholder={isPerformance ? "e.g. 50" : ""} />
                </div>
                )}
                {/* Phase 2Z.s — Performance Focus Lite fields. Both
                    optional and additive. Surfaced here so the
                    legacy KPI editor and the Performance Focus
                    "New activity" flow share one dialog. */}
                <div>
                  <Label className="eyebrow">Linked rota category</Label>
                  <Select
                    value={form.linked_category_id || "__none__"}
                    onValueChange={(v) => set({
                      linked_category_id: v === "__none__" ? "" : v,
                    })}
                  >
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="kpi-linked-category">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {/* Phase 2C - linked category is restricted to
                          Work. Performance Focus and legacy KPIs
                          report time spent on a productive Work
                          activity; pointing a KPI at Break/Away,
                          Leave or Bank Holiday is non-sensical and
                          would double-count non-productive time as
                          KPI "actual". */}
                      {(categories || [])
                        .filter((c) => c.is_active !== false
                                        && c.kind === "work")
                        .map((c) => (
                        <SelectItem key={c.category_id} value={c.category_id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#4B5773] mt-1">
                    Used by Performance Focus to compare results
                    against the time your team spent on this category.
                  </p>
                </div>
                {/* Phase 2Z.an.1 — Target basis stays visible in
                    selected-teams-only mode but is locked to
                    Per employee in V1 (Per team / Per company
                    are not supported by the dashboard yet). */}
                {(!isPerformance || true) && (
                <div data-testid="kpi-target-basis-block">
                  <Label className="eyebrow">Target basis</Label>
                  <Select
                    value={form.target_basis || "per_employee"}
                    onValueChange={(v) => set({ target_basis: v })}
                    disabled={isPerformance && form.applies_to === "selected_teams_only"}
                  >
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="kpi-target-basis"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_employee">Per employee</SelectItem>
                      <SelectItem
                        value="team"
                        disabled={isPerformance && form.applies_to === "selected_teams_only"}
                      >
                        Per team
                      </SelectItem>
                      <SelectItem
                        value="company"
                        disabled={isPerformance && form.applies_to === "selected_teams_only"}
                      >
                        Per company
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {isPerformance && form.applies_to === "selected_teams_only" && (
                    <p
                      className="text-[11px] text-[#4B5773] mt-1"
                      data-testid="kpi-target-basis-selected-hint"
                    >
                      Each employee in the selected team is measured against this target. Per team and Per company targets aren&apos;t supported in Selected teams only mode yet; use Per employee for now.
                    </p>
                  )}
                  {isPerformance && form.applies_to !== "selected_teams_only" && (
                    <p className="text-[11px] text-[#4B5773] mt-1">
                      Target rule only. Actuals are still entered per
                      employee. Team and company totals are rolled up
                      automatically.
                    </p>
                  )}
                </div>
                )}
                {/* Phase 2Z.s-2 — Performance Focus Lite. Target
                    frequency defines what the target value means
                    (e.g. 50 per week). The Enter results tab
                    multiplies this by the chosen window length to
                    compute the "Expected for this window" column.
                    Phase 2Z.an.1 — kept visible in selected-teams-
                    only mode so the manager always knows the
                    period. */}
                {isPerformance && (
                  <div data-testid="kpi-target-frequency-block">
                    <Label className="eyebrow">Target frequency</Label>
                    <Select
                      value={form.target_frequency || "per_week"}
                      onValueChange={(v) => set({ target_frequency: v })}
                    >
                      <SelectTrigger className="h-11 rounded-lg mt-2"
                        data-testid="kpi-target-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_day">Per day</SelectItem>
                        <SelectItem value="per_week">Per week</SelectItem>
                        <SelectItem value="per_month">Per month</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#4B5773] mt-1">
                      e.g. 50 per week. The report will pro-rate this
                      across the window you pick when entering results.
                    </p>
                  </div>
                )}
                {/* Phase 2Z.s-4 — Performance Focus Lite. Target
                    applies on which weekdays. Pro-rates weekly /
                    monthly targets by the business's actual
                    working week. Phase 2Z.an.1 — kept visible in
                    selected-teams-only mode. */}
                {isPerformance && (
                  <div className="col-span-2" data-testid="kpi-target-days-block">
                    <Label className="eyebrow">Target applies on</Label>
                    <div
                      className="flex flex-wrap gap-2 mt-2"
                      data-testid="kpi-target-days"
                    >
                      {[
                        [0, "Mon"], [1, "Tue"], [2, "Wed"], [3, "Thu"],
                        [4, "Fri"], [5, "Sat"], [6, "Sun"],
                      ].map(([idx, label]) => {
                        const active = (form.target_days || []).includes(idx);
                        return (
                          <button
                            key={idx}
                            type="button"
                            data-testid={`kpi-target-day-${label.toLowerCase()}`}
                            aria-pressed={active}
                            onClick={() => {
                              const cur = new Set(form.target_days || []);
                              if (cur.has(idx)) cur.delete(idx);
                              else cur.add(idx);
                              const next = Array.from(cur).sort((a, b) => a - b);
                              // At least one day must remain selected.
                              set({ target_days: next.length > 0 ? next : [idx] });
                            }}
                            className={
                              "px-3 py-1.5 text-xs rounded-lg border transition "
                              + (active
                                ? "bg-[#0F1B3D] text-white border-[#0F1B3D]"
                                : "bg-white text-[#4B5773] border-slate-200 hover:border-[#1D4ED8]")
                            }
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-[#4B5773] mt-1">
                      The target only applies on the days you tick. Mon–Fri
                      is the safe default; tick Sat/Sun for 6 or 7-day
                      activities.
                    </p>
                  </div>
                )}
              </div>
              {/* Phase 2Z.am — Performance Focus team overrides.
                  Surfaced only when editing an existing Performance
                  Focus activity (new activities have no kpi_id
                  until first save). Strictly additive; legacy KPI
                  flow does not see this block. */}
              {isPerformance && editing?.kpi_id && (
                <div
                  className="mt-4 rounded-lg border border-slate-200 bg-white p-3"
                  data-testid="kpi-team-overrides-section"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div
                        className="text-sm font-medium text-[#0F1B3D]"
                        data-testid="kpi-team-overrides-title"
                      >
                        {form.applies_to === "selected_teams_only"
                          ? "Team targets"
                          : "Team overrides"}
                        {overrides.length > 0 && (
                          <span
                            className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]"
                            data-testid="kpi-team-overrides-count"
                          >
                            {overrides.length}
                          </span>
                        )}
                      </div>
                      <p
                        className="text-[11px] text-[#4B5773] mt-0.5"
                        data-testid="kpi-team-overrides-helper"
                      >
                        {form.applies_to === "selected_teams_only"
                          ? "Add the teams this activity applies to and set their target. Teams not listed will not be measured on this activity or have results recorded against it."
                          : "Give specific teams a different target. Teams without an override use the base target above."}
                      </p>
                    </div>
                    {!addingOverride && availableTeamsForOverride.length > 0 && (
                      <Button
                        type="button"
                        onClick={() => setAddingOverride(true)}
                        data-testid="kpi-team-override-add-button"
                        className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] h-9 text-xs px-3"
                      >
                        {form.applies_to === "selected_teams_only"
                          ? "+ Add team target"
                          : "+ Add team override"}
                      </Button>
                    )}
                  </div>

                  {addingOverride && (
                    <div
                      className="mt-3 flex items-center gap-2"
                      data-testid="kpi-team-override-new-row"
                    >
                      <Select
                        value={newOverrideTeam}
                        onValueChange={setNewOverrideTeam}
                      >
                        <SelectTrigger
                          className="h-9 rounded-lg flex-1"
                          data-testid="kpi-team-override-new-team"
                        >
                          <SelectValue placeholder="Pick a team" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTeamsForOverride.map((t) => (
                            <SelectItem key={t.team_id} value={t.team_id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="any"
                        placeholder="Target"
                        value={newOverrideValue}
                        onChange={(e) => setNewOverrideValue(e.target.value)}
                        data-testid="kpi-team-override-new-value"
                        className="h-9 rounded-lg w-28"
                      />
                      <Button
                        type="button"
                        onClick={createOverride}
                        disabled={overridesBusy}
                        data-testid="kpi-team-override-new-save"
                        className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] h-9 text-xs px-3"
                      >
                        {overridesBusy ? "Saving…" : "Save"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setAddingOverride(false);
                          setNewOverrideTeam("");
                          setNewOverrideValue("");
                        }}
                        data-testid="kpi-team-override-new-cancel"
                        className="rounded-lg h-9 text-xs px-3"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}

                  {overrides.length === 0 && !addingOverride && (
                    <p
                      className="text-[11px] text-[#4B5773] mt-3 italic"
                      data-testid="kpi-team-overrides-empty"
                    >
                      {form.applies_to === "selected_teams_only"
                        ? "No team targets yet. Add at least one team target to save this activity."
                        : "No team overrides yet. Every team uses the base target."}
                    </p>
                  )}

                  {overrides.length > 0 && (
                    <div
                      className="mt-3 space-y-2"
                      data-testid="kpi-team-overrides-list"
                    >
                      {overrides.map((o) => (
                        <OverrideRow
                          key={o.override_id}
                          override={o}
                          unit={form.unit}
                          frequency={form.target_frequency || "per_week"}
                          busy={overridesBusy}
                          onSave={(v) => updateOverrideValue(o.override_id, v)}
                          onRemove={() => removeOverride(o.override_id)}
                        />
                      ))}
                    </div>
                  )}

                  {availableTeamsForOverride.length === 0
                    && overrides.length > 0
                    && !addingOverride && (
                    <p
                      className="text-[11px] text-[#4B5773] mt-3 italic"
                      data-testid="kpi-team-overrides-all-set"
                    >
                      All teams have an override.
                    </p>
                  )}
                </div>
              )}
              {/* Warning / Critical thresholds are advanced and
                  rarely needed for V1. In Performance Focus mode
                  they live behind a collapsed disclosure so the
                  manager sees a clean primary form. Legacy KPI
                  mode keeps them inline as before. */}
              {isPerformance ? (
                <details
                  className="mt-4 rounded-lg border border-slate-200 bg-[#F6F9FE] p-3"
                  data-testid="kpi-advanced-thresholds-disclosure"
                >
                  <summary className="cursor-pointer text-xs text-[#4B5773]">
                    Advanced thresholds (optional)
                  </summary>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="eyebrow">Warning threshold</Label>
                      <Input type="number" step="any" data-testid="kpi-warn"
                        value={form.warning_threshold ?? ""}
                        onChange={(e) => set({ warning_threshold: e.target.value === "" ? null : Number(e.target.value) })}
                        className="h-11 rounded-lg mt-2" />
                    </div>
                    <div>
                      <Label className="eyebrow">Critical threshold</Label>
                      <Input type="number" step="any" data-testid="kpi-crit"
                        value={form.critical_threshold ?? ""}
                        onChange={(e) => set({ critical_threshold: e.target.value === "" ? null : Number(e.target.value) })}
                        className="h-11 rounded-lg mt-2" />
                    </div>
                  </div>
                </details>
              ) : (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="eyebrow">Warning threshold</Label>
                    <Input type="number" step="any" data-testid="kpi-warn"
                      value={form.warning_threshold ?? ""}
                      onChange={(e) => set({ warning_threshold: e.target.value === "" ? null : Number(e.target.value) })}
                      className="h-11 rounded-lg mt-2" />
                  </div>
                  <div>
                    <Label className="eyebrow">Critical threshold</Label>
                    <Input type="number" step="any" data-testid="kpi-crit"
                      value={form.critical_threshold ?? ""}
                      onChange={(e) => set({ critical_threshold: e.target.value === "" ? null : Number(e.target.value) })}
                      className="h-11 rounded-lg mt-2" />
                  </div>
                </div>
              )}
              <p className="text-[11px] text-[#4B5773] mt-3">
                {form.direction === "target_band"
                  ? "Warning/Critical are the absolute deviation widths from Target before that status triggers."
                  : "Status is derived from the latest observation. Not enforced anywhere today."}
              </p>
            </TabsContent>

            {/* Scope & tags tab — hidden in Performance Focus mode. */}
            {!isPerformance && (
            <TabsContent value="scope">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="eyebrow">Scope</Label>
                  <Select value={form.scope}
                    onValueChange={(v) => set({ scope: v, scope_id: "" })}>
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="kpi-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCOPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.scope !== "company" && (
                  <div>
                    <Label className="eyebrow">Scope target</Label>
                    <Select value={form.scope_id}
                      onValueChange={(v) => set({ scope_id: v })}>
                      <SelectTrigger className="h-11 rounded-lg mt-2"
                        data-testid="kpi-scope-id"><SelectValue placeholder={`Pick a ${form.scope}`} /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {scopeOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <Label className="eyebrow">Tags</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input value={tagInput}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="ops, finance…"
                    data-testid="kpi-tag-input"
                    className="h-10 rounded-lg" />
                  <Button type="button" onClick={addTag} data-testid="kpi-tag-add"
                    className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Add</Button>
                </div>
                {(form.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="kpi-tags">
                    {form.tags.map((t) => (
                      <button type="button" key={t} onClick={() => removeTag(t)}
                        data-testid={`kpi-tag-${t}`}
                        className="text-xs px-2.5 py-1 rounded-full border bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB] hover:bg-red-50 hover:text-red-700 hover:border-red-200">
                        {t} ×
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-[#F6F9FE] border border-slate-200">
                <div className="eyebrow mb-2">Source policy</div>
                <p className="text-[11px] text-[#4B5773] mb-2">
                  Values are recorded manually so you stay in control of what's
                  tracked. CSV imports and API integrations are on the roadmap.
                </p>
                <Label className="eyebrow">Preferred source</Label>
                <Select value={form.preferred_source}
                  onValueChange={(v) => set({ preferred_source: v })}>
                  <SelectTrigger className="h-10 rounded-lg mt-2"
                    data-testid="kpi-pref-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["manual", "csv_import", "api", "auto_computed", "external"].map((s) =>
                      <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            )}
          </Tabs>

          <DialogFooter className="mt-4">
            <Button type="submit" disabled={busy} data-testid="kpi-submit"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Saving…" : editing ? "Save changes" : `Create ${noun}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Phase 2Z.am — Inline editor row for a single team override.
// Local state so each row's pending value doesn't churn the
// parent's overrides list while the manager is typing.
function OverrideRow({ override, unit, frequency, busy, onSave, onRemove }) {
  const [value, setValue] = useState(String(override.target_value ?? ""));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setValue(String(override.target_value ?? ""));
  }, [override.target_value]);
  const teamName = override.scope_name || override.scope_id;
  const freqLabel = (frequency || "per_week")
    .replace("per_", "per ");
  return (
    <div
      className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-[#F6F9FE]"
      data-testid={`kpi-team-override-row-${override.scope_id}`}
    >
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-medium text-[#0F1B3D] truncate"
          data-testid={`kpi-team-override-team-name-${override.scope_id}`}
        >
          {teamName}
        </div>
        {!editing && (
          <div className="text-[11px] text-[#4B5773]">
            <span data-testid={`kpi-team-override-value-${override.scope_id}`}>
              {override.target_value}
            </span>
            {" "}{unit || "count"} {freqLabel}
          </div>
        )}
      </div>
      {editing ? (
        <>
          <Input
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={`kpi-team-override-edit-value-${override.scope_id}`}
            className="h-9 rounded-lg w-24"
          />
          <Button
            type="button"
            onClick={async () => {
              await onSave(value);
              setEditing(false);
            }}
            disabled={busy}
            data-testid={`kpi-team-override-save-${override.scope_id}`}
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] h-9 text-xs px-3"
          >
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setValue(String(override.target_value ?? ""));
              setEditing(false);
            }}
            data-testid={`kpi-team-override-cancel-${override.scope_id}`}
            className="rounded-lg h-9 text-xs px-3"
          >
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => setEditing(true)}
            data-testid={`kpi-team-override-edit-${override.scope_id}`}
            className="rounded-lg h-9 text-xs px-3"
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onRemove}
            disabled={busy}
            data-testid={`kpi-team-override-remove-${override.scope_id}`}
            className="rounded-lg h-9 text-xs px-3 text-red-600 border-red-200 hover:bg-red-50"
          >
            Remove
          </Button>
        </>
      )}
    </div>
  );
}
